import { useRecoilValue } from 'recoil';
import { organizationAtom } from 'services/organization/atom';
import { userAtom } from 'services/user/atoms';
import {
  useSearchCandidateBatchesTotalCountQuery,
  useSearchCandidatesTotalCountQuery,
  useSearchSourcingCandidatesTotalCountQuery
} from 'generated-graphql/hooks/apollo-hooks';
import {
  CandidateBatchStatus,
  SearchableCandidateBatchFilterInput,
  SearchableCandidateFilterInput,
  SearchableSourcedCandidateFilterInput,
  SourcedCandidateStatus
} from 'generated-graphql/types';

type UseTabTotalsProps = {
  isAllCampaigns: boolean;
};

export const useTabTotals = ({ isAllCampaigns }: UseTabTotalsProps) => {
  const organization = useRecoilValue(organizationAtom);
  const user = useRecoilValue(userAtom);

  // Filter for candidate batches (Analyses tab)
  const candidateBatchFilter: SearchableCandidateBatchFilterInput = {
    organizationId: { eq: organization?.id ?? '' },
    status: { ne: CandidateBatchStatus.Archived }
  };

  if (!isAllCampaigns && user?.id) {
    candidateBatchFilter['userId'] = { eq: user.id };
  }

  const candidateFilter: SearchableCandidateFilterInput = {
    organizationId: { eq: organization?.id ?? '' }
  };

  const sourcedCandidateFilter: SearchableSourcedCandidateFilterInput = {
    organizationId: { eq: organization?.id ?? '' },
    status: { ne: SourcedCandidateStatus.AwaitingEnrichment }
  };

  // Fetch all three totals in parallel
  const { data: candidateBatchData, loading: candidateBatchLoading } =
    useSearchCandidateBatchesTotalCountQuery({
      variables: {
        filter: candidateBatchFilter
      },
      skip: !organization?.id
    });

  const { data: candidateData, loading: candidateLoading } =
    useSearchCandidatesTotalCountQuery({
      variables: {
        filter: candidateFilter
      },
      skip: !organization?.id
    });

  const { data: sourcedCandidateData, loading: sourcedCandidateLoading } =
    useSearchSourcingCandidatesTotalCountQuery({
      variables: {
        filter: sourcedCandidateFilter
      },
      skip: !organization?.id
    });

  return {
    analysesTotal: candidateBatchData?.searchCandidateBatches?.total ?? 0,
    applicantsTotal: candidateData?.searchCandidates?.total ?? 0,
    sourcedCandidatesTotal:
      sourcedCandidateData?.searchSourcedCandidates?.total ?? 0,
    loading:
      candidateBatchLoading || candidateLoading || sourcedCandidateLoading
  };
};
